import { createSignal } from 'solid-js'

function detectEnv() {
  const host = location.hostname
  if (host.startsWith('stg.')) return 'Stg.'
  if (host.startsWith('dev.') || host === 'localhost') return 'Dev'
  return ''
}

const raw = window.user ?? {}
if (!raw.env) raw.env = detectEnv()
const [account, setAccount] = createSignal(raw)

export default account

export async function logout() {
  setAccount({})
  return fetch('/api/v1/logout').finally(() => {
    document.location.reload()
  })
}

export async function keepalive() {
  if (account().username) {
    return fetch('/api/v1/keepalive').catch(() => { }).finally(() => {
      setTimeout(() => keepalive(), 3_600_000)
    })
  }
}

// PSD-344（P2 Permission Rename）：CSV v3.1 正名前後的新舊鍵對照。
//
// v3.1 改名的 22 把鍵（260819170816_update_perm_keys_v31_rename.sql）全數收錄。
// 其中 7 組（view oplogs、staff role/staff user 系列）與 BI 的鍵同名，但那只是碰巧撞名：
// CRM 的 perm.go 與 migration 確實把它們改了名，屬於改名組合，一樣要收。
// 跨產品不會誤傷：權限依 scope 存取（staff_role_permissions.scope，由 process 的
// SCOPE 設定決定），CRM 進程只拿得到 crm scope 的鍵、BI 只拿得到 bi scope 的鍵，
// 別名只會在自己 scope 的新舊鍵之間兜底。
// rename 必須 Backend perm.go → DB Role/Permission migration → Frontend 依序同步；
// 前端先鋪「雙向別名」——access() 對任一邊的鍵名都認帳，後端/DB 改名當下前端不用同步發版、
// 也不會發生「前端先改、非 Admin 全被鎖住」。全部改名完成後可移除此表。
const PERM_RENAMES = {
  'view lines': 'view line list',
  'view merchants': 'view merchant list',
  'view campaigns': 'view campaign list',
  'view products': 'view product list',
  'view providers': 'view product providers',
  'view line remark': 'view line remarks',
  'view merchant financial groupings': 'view merchant financial grouping',
  'setup merchant pricing': 'set up merchant pricing',
  'handover line am staff': 'hand over line to am',
  'manage public tag': 'manage public tags',
  'manage private tag': 'manage private tags',
  'add provider': 'add product provider',
  'edit provider': 'edit product provider',
  'delete provider': 'delete product provider',
  'edit task': 'edit and copy task',
  'view oplogs': 'view operation log',
  'view staff roles': 'view roles',
  'add staff role': 'add role',
  'edit staff role': 'edit role',
  'view staff users': 'view staff list',
  'add staff user': 'add staff',
  'edit staff user': 'edit staff',
}
const PERM_ALIASES = { ...PERM_RENAMES }
for (const [oldName, newName] of Object.entries(PERM_RENAMES)) PERM_ALIASES[newName] = oldName

function hasAny(list, actions) {
  for (const action of actions) {
    if (list.indexOf(action) > -1) return true
    const alias = PERM_ALIASES[action]
    if (alias && list.indexOf(alias) > -1) return true
  }
  return false
}

// 目前產品線；null 時 access() 只看通用層權限
const [productLine, setProductLine] = createSignal(null)
export { productLine, setProductLine }

// account().rbac 只有 BI 後端會給；沒有 rbac 是 CRM 路徑（扁平 permissions 陣列），行為不可改變
export function access(...actions) {
  if (isAdmin()) return true
  const { rbac } = account()
  if (!rbac) return hasAny(account().permissions ?? [], actions)
  const p = productLine()
  const list = p ? [...(rbac.global_permissions ?? []), ...(rbac.product_lines?.[p] ?? [])] : (rbac.global_permissions ?? [])
  return hasAny(list, actions)
}

// 只看通用層權限（無 rbac 時退回扁平 permissions）
export function accessGlobal(...actions) {
  if (isAdmin()) return true
  const { rbac } = account()
  return hasAny(rbac ? (rbac.global_permissions ?? []) : (account().permissions ?? []), actions)
}

// 只看指定產品線；product_lines[X] 後端已合併通用層，不用再併 global
export function accessIn(product, ...actions) {
  if (isAdmin()) return true
  const { rbac } = account()
  return hasAny(rbac ? (rbac.product_lines?.[product] ?? []) : (account().permissions ?? []), actions)
}

// 已登入但後端權限載入失敗
export function hasRbacError() {
  return account().rbac_error === true
}

export function isFillingInFor(code) {
  return (account().filling ?? []).indexOf(code) > -1
}

// 檢查當前用戶是否是目標用戶，或是其代理人
export function isUserOrFillingInFor(code) {
  if (!code) return false
  if (account().code === code) return true
  return isFillingInFor(code)
}

// 檢查當前用戶是否是任一目標用戶，或是其代理人
export function isAnyUserOrFillingInFor(codes) {
  return codes.some((code) => isUserOrFillingInFor(code))
}

// 檢查是否為 admin
export function isAdmin() {
  const a = account()
  return (a.roles?.includes('admin') ?? false) || a.rbac?.is_admin === true
}

// 檢查是否為 lead（roles 中包含 xx-lead）
export function isLead() {
  const roles = account().roles ?? []
  return roles.some((role) => role.endsWith('-lead'))
}

// 檢查當前用戶是否為指定 staff 的 lead
// 規則：登入者的 lead 角色前綴（如 am-lead → am）必須存在於 staffRoles 中
export function isLeadOfStaff(staffRoles) {
  if (!staffRoles || staffRoles.length === 0) return false

  const myRoles = account().roles ?? []

  // 找出我的 lead 角色（以 -lead 結尾的）
  const myLeadRoles = myRoles.filter((role) => role.endsWith('-lead'))
  if (myLeadRoles.length === 0) return false

  // 從每個 lead 角色提取前綴（如 'am-lead' → 'am'）
  const myLeadPrefixes = myLeadRoles.map((role) => role.replace(/-lead$/, ''))

  // 檢查 staffRoles 是否包含任一前綴
  return myLeadPrefixes.some((prefix) => staffRoles.includes(prefix))
}

/**
 * 檢查當前用戶是否可以編輯任務相關項目（To-Do、Comment 等）
 * 通用權限檢查函數，整合多個組件的重複邏輯
 *
 * @param {Object} params - 權限檢查參數
 * @param {boolean} params.isTaskEditor - 是否已經是 task 編輯者（由父組件預先計算）
 * @param {string} params.taskOwnerCode - task owner / staff（被指派負責人）的 code
 * @param {string} params.assignerCode - 任務指派者的 code
 * @param {string[]} params.participantCodes - 參與者的 code 陣列
 * @returns {boolean} 是否有編輯權限
 */
export function canEditTaskItem({ isTaskEditor, taskOwnerCode, assignerCode, participantCodes = [] }) {
  // admin 有所有權限
  if (isAdmin()) return true

  // 已經是 task 編輯者（由父組件判斷，包含 lead 等檢查）
  if (isTaskEditor) return true

  // 檢查是否為 task owner / staff（或其代理人）
  if (isUserOrFillingInFor(taskOwnerCode)) return true

  // 檢查是否為 assigner（任務指派者）或其代理人
  if (isUserOrFillingInFor(assignerCode)) return true

  // 檢查是否為參與者（或其代理人）
  if (participantCodes.length > 0 && isAnyUserOrFillingInFor(participantCodes)) return true

  return false
}
